/**
 * Single source of truth for the Clerk prebuilt UI: Hungarian copy and appearance.
 * Components and `main.ts` import from here — no per-page Clerk config.
 */
import { huHU } from '@clerk/localizations/hu-HU'
import { setProperty } from 'dot-prop'

type ClerkLocalization = typeof huHU

type LocalizationPath<T> = {
  [K in keyof T & string]: NonNullable<T[K]> extends string ? K : `${K}.${LocalizationPath<NonNullable<T[K]>>}`
}[keyof T & string]

/** Hungarian Clerk copy. Deep copy, because `huHU` is a shared module object. */
export const clerkLocalization = structuredClone(huHU)

function set(path: LocalizationPath<ClerkLocalization>, value: string): void {
  setProperty(clerkLocalization, path, value)
}

set('formFieldInputPlaceholder__emailAddress', 'Add meg az email címed')
set('formFieldInputPlaceholder__password', 'Add meg a jelszavad')
set('signIn.start.title', 'Bejelentkezés az {{applicationName}} fiókba')
set('signIn.emailCode.subtitle', 'Írd be a kódot a belépéshez')
set('signIn.phoneCode.subtitle', 'Írd be a kódot a belépéshez')
set('signUp.start.subtitle', 'Szia! A szolgáltatói fiók létrehozásához add meg az adataid.')
set('signUp.start.subtitleCombined', 'Szia! A szolgáltatói fiók létrehozásához add meg az adataid.')
// `hu-HU` has no copy for this code, so Clerk would fall back to the English API message.
set(
  'unstable__errors.external_account_not_found',
  'Ehhez a fiókhoz még nem tartozik regisztráció. Hozz létre fiókot a folytatáshoz.',
)

/** Clerk prebuilt UI: drop the default white card panel (background + shadow). */
export const clerkAuthAppearance = {
  theme: 'simple' as const,
  variables: {
    colorPrimary: 'black',
    colorShadow: 'transparent',
    borderWidth: 0,
    inputRadius: '100px',
    borderRadius: '24px',
  },
  elements: {
    card: {
      paddingLeft: 0,
      paddingRight: 0,
      background: 'transparent',
      boxShadow: 'none',
      borderWidth: 0,
    },
    button: {
      background: 'white',
    },
    rootBox: {
      maxWidth: '100%',
    },
    cardBox: {
      border: 'none',
      maxWidth: '100%',
    },
    formButtonPrimary: {
      background: 'linear-gradient(51.69deg, #FF8662 26.38%, #FC62DC 77.84%)',
      border: 'none',
      color: 'white',
      borderRadius: '100px',
      fontSize: '15px',
      padding: '10px 40px',
      width: 'auto',
    },
    formFieldLabelRow__identifier: {
      justifyContent: 'center',
      position: 'relative',
      '& .cl-lastAuthenticationStrategyBadge': {
        position: 'absolute',
        insetInlineEnd: '12px',
        top: 'calc(100% + 8px)',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        zIndex: 1,
      },
    },
    /** 16px keeps iOS Safari from zooming into the field on focus. */
    formFieldInput: {
      fontSize: 'var(--font-size-body)',
    },
    /** Match `OtpInput.vue` pill cells on email/phone code steps. */
    otpCodeField: {
      marginBlock: '10px',
    },
    otpCodeFieldInputs: {
      display: 'flex',
      justifyContent: 'center',
      gap: '6px',
    },
    otpCodeFieldInput: {
      width: '2.5rem',
      height: '2.5rem',
      minWidth: '2.5rem',
      minHeight: '2.5rem',
      flexShrink: '0',
      boxSizing: 'border-box',
      backgroundColor: 'var(--color-surface)',
      borderRadius: 'var(--radius-pill)',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      fontSize: 'var(--font-size-display)',
      fontFamily: 'monospace',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--color-text)',
      boxShadow: 'none',
      '&[data-focus-within="true"]': {
        borderColor: 'var(--color-text)',
        boxShadow: 'none',
      },
      /** Clerk caret inside the active empty cell. */
      '&[data-focus-within="true"] > div > div': {
        backgroundColor: 'var(--color-text)',
      },
    },
    buttonArrowIcon: {
      display: 'none',
    },
    footer: {
      backgroundImage: 'none',
    },
  },
}
